import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import WhyBookWithUs from '../content/WhyBookWithUs';
import FeaturedCuisines from '../FeaturedCuisines';
import HowItWorks from '../content/HowItWorks';
import CateringCta from '../content/CateringCta';
import TripleCta from '../content/TripleCta';
import Testimonials from '../content/Testimonials';
import AsianFaq from '../faqs/AsianFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

function AsianCatering() {
  const { vehicle_type, site_title, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  const featuredTrucks = { food_categories: 'asian' };

  return (
    <div className='CateringPage'>
      <Meta
        title={`Asian Food ${vehicle_type} Catering`}
        type='website'
        canonical='/food-truck-catering/asian-catering'
        image='https://static.seattlefoodtruck.com/photos/new-asian-header.webp'
        description={`Book Asian food ${vehicleType} catering in ${city_name} for corporate events, weddings, and parties. Sushi, dumplings, Korean BBQ & more. Get free quotes.`}
        structuredData={{
          cateringService: {
            name: `Asian Food ${vehicle_type} Catering in ${city_name}`,
            description: `Book Asian food ${vehicleType} catering in ${city_name}. Sushi, dumplings, Korean BBQ and more.`,
            url: '/food-truck-catering/asian-catering',
            serviceType: 'Asian Food Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Asian Catering', item: '/food-truck-catering/asian-catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/new-asian-header.webp' noLazy />
          <div className={styles.Cta_copy}>
            <h1>Asian Food {vehicle_type} Catering for Your Event</h1>
            <p>Looking for bold flavors and authentic Asian cuisine for your next event? Whether it&#39;s a <Link to='/food-truck-catering/corporate-catering'>corporate gathering</Link>, <Link to='/food-truck-catering/wedding-catering'>wedding</Link>, <Link to='/food-truck-catering/birthday-parties'>birthday party</Link>, or <Link to='/food-truck-catering/community-events'>festival</Link>, our network of top-rated Asian food {vehicleType}s delivers everything from savory dumplings and sushi rolls to spicy Korean BBQ and Thai curries. {site_title} makes it effortless to find and book the best Asian food {vehicleType}s in town. Simply tell us about your event, receive tailored quotes, and secure your perfect match — it&#39;s that easy!</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Start Your Asian Catering Request</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <TripleCta
        cuisine='asian'
        image='https://static.seattlefoodtruck.com/photos/asian4.webp'
        optionsText='Options include sushi, dumplings, poke bowls, Korean BBQ, ramen, and more.'
      />

      <FeaturedTrucks title={`Featured Asian Food ${vehicle_type}s`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Book an Asian Food ${vehicle_type} for Catering?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>What Makes Us Different?</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/asian1.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Authentic & Diverse Asian Cuisine</h4>
            <p>
              {site_title} connects you with the best Asian food {vehicleType}s in {city_name}, offering a wide variety of flavors from across the continent. Whether you&#39;re craving classic Chinese stir-fries, rich Japanese ramen, bold Korean BBQ, or fragrant Thai curries, our vendors use fresh, high-quality ingredients to create an unforgettable dining experience. Many of our {vehicleType}s offer vegetarian, vegan, and gluten-free options, ensuring there&#39;s something for everyone.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/asian2.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Effortless Asian Catering for Any Event</h4>
            <p>
              Booking an Asian food {vehicleType} should be as easy as enjoying a bowl of pho. Our seamless process allows you to enter your event details, receive multiple quotes from verified Asian food vendors, and book in minutes. Whether you need an elegant sushi bar for a wedding or a fast and flavorful noodle station for a corporate event, we make it happen stress-free.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/asian3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Customizable Menus & Dietary Options</h4>
            <p>
              From customizable poke bowls to dumpling platters, our Asian food {vehicleType}s offer menus tailored to your guests&#39; preferences. Need halal, vegetarian, or gluten-free options? Want to mix cuisines for a diverse street food experience? Our food {vehicleType}s can accommodate all special requests, ensuring your event stands out.
            </p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent title={`Asian Food ${vehicle_type} Catering for Any Event`} />

      <Testimonials title="Join Thousands Who've Booked Asian Catering With Us!" />

      <CostEstimator title='Asian Catering Cost Estimator' />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <AsianFaq />
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta />
      </section>
    </div>
  );
}

export default AsianCatering;
